import React, { memo } from "react";
import { Button, Col, InputNumber, message, Row, Table, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
const { Title, Text } = Typography;
import PropTypes from "prop-types";
import { currencyGetter } from "../../../../utils/BankCurrencyUtils";
import { formatCurrency, formatToTwoDecimalPlaces } from "../../utils";
import { formatShowStandarDate } from "../../../../utils/Formaters";

export const BankReconciliationTable = memo(({ 
	invoicesToSend = {},
	bankTransaction = {},
	currency = "",
	onUpdateInvoiceAmount = () => {},
	onRemoveInvoice = () => {},
	isEditable = false,
}) => {
	const { 
		amount = 0,
		position = "",
		transaction_number = "",
		reconciliation: { invoices: originalInvoices = {}, transaction_consumed = false } = {},
	} = bankTransaction;
		
	const totalAmountInvoicesForReconciliation = Object.values(invoicesToSend).reduce(
		(accumulator, invoice) => accumulator += invoice.amount
		, 0);
	
	const remainingAmount = formatToTwoDecimalPlaces(amount) - formatToTwoDecimalPlaces(totalAmountInvoicesForReconciliation);
		
	const NoBankTransactionAvaliable = (<>
		<br/>
		<br/>
		<br/>
		<br/>
		<br/>
		<br/>
		<Title level={3} style={{textAlign: "center", color: "blue"}}>Porfavor presiona click en una Transacción :)</Title>
	</>);
		
	const columns = [
		{
			title: "Fecha",
			dataIndex: "date",
			width: 100,
			align: "center",
			render: (_, record) => (
				<span style={{ whiteSpace: "nowrap" }}>
					<Text>{formatShowStandarDate(record.date)}</Text>
				</span>
			),
		},
		{
			title: "N. Referencia",
			dataIndex: "reference",
			width: 140,
			align: "center",
			render: (_, record) => (
				<Text>{record.reference}</Text>
			),
		},
		{
			title: "Total",
			dataIndex: "total",
			width: 140,
			align: "center",
			render: (_, record) => (
				<span style={{ whiteSpace: "nowrap" }}>
					<Text>{currencyGetter[currency]} {formatCurrency(record.total)}</Text>
				</span>
			),
		},
		{
			title: "A Pagar",
			dataIndex: "amount",
			width: 140,
			align: "center",
			render: (_, record) => {
				const maximumValue = formatToTwoDecimalPlaces(record.initial_pendient) + formatToTwoDecimalPlaces(originalInvoices[record.id]?.amount ?? 0);
				
				return (
					<InputNumber
						disabled={isEditable ? false : (record.notAtInvoices ?? false) || transaction_consumed}
						style={{ width: "100%", borderRadius: 5 }}
						size="small"
						prefix={`${currencyGetter[currency]} ${position === "in" ? "+" : "-"}`}
						min={0.00}
						max={maximumValue}
						precision={2}
						step={0.01}
						value={record?.amount}
						onChange={(value) => {										
							const invoicesToSendForPrediction = {...invoicesToSend, [record.id]: {...record, amount: formatToTwoDecimalPlaces(value)}};
							
							const predictionRemainingAmount = Object.values(invoicesToSendForPrediction).reduce(
								(accumulator, invoice) => accumulator += invoice.amount
								, 0);
									
							if(predictionRemainingAmount > amount){
								message.error("Ya no hay más saldo en la transacción para la concilación bancaria");
								return;
							}
								
							onUpdateInvoiceAmount({value, invoiceId: record?.id});
						}}
						status={record?.amount === 0.00 ? "error" : ""}
						controls={false}					
					/>
				);
			},
		},
		{
			title: "Pendiente",
			dataIndex: "pendinet",
			width: 140,
			align: "center",
			render: (_, record) => (
				<Text>{record.notAtInvoices ? "No Cargado" : `${currencyGetter[currency]} ${formatCurrency(record.pendient)}`}</Text>
			),
		},
		{
			title: "Total Pagado",
			dataIndex: "total_payed",
			width: 140,
			align: "center",
			render: (_, record) => (
				<Text>{record.notAtInvoices ? "No Cargado" : `${formatToTwoDecimalPlaces(record.total_payed)} %`}</Text>
			),
		},
		{
			title: "Acciones",
			dataIndex: "actions",
			width: 140,
			align: "center",
			render: (_, record) => (
				<Button 
					icon={<DeleteOutlined />} 
					danger
					onClick={() => onRemoveInvoice({invoiceId: record.id, checked: false})}
				>Quitar</Button>
			),
		},
	];
	
	const ReconciliationView = (<>
		<Row justify="center" align="middle" style={{ flexWrap: "wrap" }}>
			<Col style={{ marginTop: "-5px", marginBottom: "10px", marginLeft: "20px", marginRight: "20px" }}>
				<Title level={4} style={{ display: "inline", fontWeight: "bold" }}>Transacción: </Title>
				<Text style={{fontSize: "17px"}}>{transaction_number}</Text>				
			</Col>
			<Col style={{ marginTop: "-5px", marginBottom: "10px", marginLeft: "20px", marginRight: "20px" }}>
				<Title level={4} style={{ display: "inline", fontWeight: "bold" }}>Saldo: </Title>
				<Text style={{fontSize: "17px"}}>{currencyGetter[currency]}{formatCurrency(amount)}</Text>				
			</Col>
			<Col style={{ marginTop: "-5px", marginBottom: "10px", marginLeft: "20px", marginRight: "20px" }}>
				<Title level={4} style={{ display: "inline", fontWeight: "bold" }}>Saldo Restante: </Title>
				<Text style={{fontSize: "17px"}}>
					{currencyGetter[currency]}
					{formatCurrency(remainingAmount)}
				</Text>
			</Col>
			<Col style={{ marginTop: "-5px", marginBottom: "10px", marginLeft: "20px", marginRight: "20px" }}>
				<Title level={4} style={{ display: "inline", fontWeight: "bold" }}>Tipo: </Title>
				<Text style={{fontSize: "17px"}}>{`${position === "in" ? "Crédito" : "Débido"}`}</Text>			
			</Col>
		</Row>

		<Table
			size="small"
			pagination={false}
			style={{ marginRight: "15px", marginLeft: "15px", textAlign: "start", overflowY: "auto" }}
			bordered
			columns={columns}
			dataSource={Object.values(invoicesToSend)}
			footer={() => (
				<Row justify="center" align="middle">
					<Col span={24} style={{ textAlign: "center" }}>
						<Text>
							Selecciona las facturas en la ventana de abajo para incluirlas en la concilación bancaria {":)"}
						</Text>
					</Col>
				</Row>
			)}
		/>
	</>);
	
	return (
		<>
			{JSON.stringify(bankTransaction) === "{}" && NoBankTransactionAvaliable}
			{JSON.stringify(bankTransaction) !== "{}" && ReconciliationView}
		</>
	);
});

BankReconciliationTable.displayName = "BankReconciliationTable";

BankReconciliationTable.propTypes = {
	token: PropTypes.string.isRequired,
	bankTransaction: PropTypes.shape({
		id: PropTypes.string,
		amount: PropTypes.number,
		position: PropTypes.string,
		reconciliation: PropTypes.object,
		transaction_number: PropTypes.string,
	}).isRequired,
	invoicesToSend: PropTypes.object.isRequired,
	currency: PropTypes.string.isRequired,
	onUpdateInvoiceAmount: PropTypes.func.isRequired,
	onRemoveInvoice: PropTypes.func.isRequired,
	onSetInvoicesToSend: PropTypes.func.isRequired,
	isEditable: PropTypes.bool,
};